<template>
  <div class="spending-manage">
    <page-header title="结算单管理" />

    <el-tabs v-model="mainTab" type="card" @tab-click="onMainTabChange">
      <!-- Tab 1: 结算单管理 -->
      <el-tab-pane label="结算单" name="orders">
        <el-tabs v-model="settlementType" type="border-card" @tab-click="onTypeTabChange">
          <el-tab-pane label="供应商结算" name="SUPPLIER">
            <spending-order-list
              v-if="mainTab === 'orders' && settlementType === 'SUPPLIER'"
              ref="supplierList"
              settlement-type="SUPPLIER"
              @create="openCreate"
              @detail="openDetail"
              @submit="quickSubmit"
              @history="openHistory" />
          </el-tab-pane>
          <el-tab-pane label="劳务结算" name="LABOR">
            <spending-order-list
              v-if="mainTab === 'orders' && settlementType === 'LABOR'"
              ref="laborList"
              settlement-type="LABOR"
              @create="openCreate"
              @detail="openDetail"
              @submit="quickSubmit"
              @history="openHistory" />
          </el-tab-pane>
        </el-tabs>
      </el-tab-pane>

      <!-- Tab 2: 审核记录 -->
      <el-tab-pane label="审核记录" name="audit">
        <div class="audit-finder">
          <el-input
            v-model="auditOrderId"
            placeholder="输入结算单 ID 查询审核记录"
            style="width:320px"
            size="small"
            clearable
            @keyup.enter.native="loadAuditOrderId">
            <el-button slot="append" icon="el-icon-search" @click="loadAuditOrderId" />
          </el-input>
          <span class="tip">从结算单列表点击"审核记录"可快速跳转</span>
        </div>
        <audit-timeline v-if="auditViewOrderId" :order-id="auditViewOrderId" />
        <div v-else class="empty">请输入或选择一个结算单</div>
      </el-tab-pane>

      <!-- Tab 3: 统计看板 -->
      <el-tab-pane label="统计看板" name="stats">
        <spending-statistics ref="stats" />
      </el-tab-pane>
    </el-tabs>

    <spending-order-dialog
      :visible.sync="createVisible"
      :default-type="settlementType"
      :org-options="orgOptions"
      :supplier-options="supplierOptions"
      :collect-way-options="collectWayOptions"
      :prefill-meeting="prefillMeeting"
      @created="onCreated" />

    <spending-order-detail
      :visible.sync="detailVisible"
      :order-id="detailOrderId"
      @ai-audit="openAiAudit"
      @changed="reloadCurrentList" />

    <el-drawer title="AI 审核结果" :visible.sync="aiAuditVisible" direction="rtl" size="700px" append-to-body>
      <ai-audit-panel v-if="aiAuditOrderId" :order-id="aiAuditOrderId" />
    </el-drawer>
  </div>
</template>

<script>
import SpendingOrderList from './components/SpendingOrderList.vue';
import SpendingOrderDialog from './components/SpendingOrderDialog.vue';
import SpendingOrderDetail from './components/SpendingOrderDetail.vue';
import AuditTimeline from './components/AuditTimeline.vue';
import SpendingStatistics from './components/SpendingStatistics.vue';
import AiAuditPanel from '@/components/AiAuditPanel.vue';
import PageHeader from '@/components/PageHeader.vue';
import { submitSpendingOrder, getMeetingDetail } from '@/request/api.js';
import { getOrgListCached, getSupplierListCached, getCollectWayCached } from '@/utils/optionsCache.js';

export default {
  name: 'SpendingManage',
  components: {
    SpendingOrderList, SpendingOrderDialog, SpendingOrderDetail,
    AuditTimeline, SpendingStatistics, AiAuditPanel, PageHeader
  },
  data() {
    return {
      mainTab: 'orders',
      settlementType: 'SUPPLIER',
      createVisible: false,
      detailVisible: false,
      detailOrderId: null,
      auditOrderId: '',
      auditViewOrderId: null,
      aiAuditVisible: false,
      aiAuditOrderId: null,
      // 共享下拉数据(进入页面时一次性加载, 全程缓存)
      orgOptions: [],
      supplierOptions: [],
      collectWayOptions: [],
      // G11: 从"我的会议"跳过来时预填的会议信息
      prefillMeeting: null
    };
  },
  created() {
    this.preloadOptions();
  },
  mounted() {
    // G11: URL 带 fromMeetingId 时, 自动拉会议详情并打开新建对话框
    const fromId = this.$route.query.fromMeetingId;
    if (fromId) {
      this.loadMeetingAndOpenCreate(fromId);
    }
  },
  methods: {
    async preloadOptions() {
      // 走 localStorage 缓存 (4 小时 TTL),命中时同步返回零延迟
      // Object.freeze 阻止 Vue 深度响应式化, 大列表性能关键
      const freezeList = list => {
        if (!Array.isArray(list)) return [];
        list.forEach(Object.freeze);
        return Object.freeze(list);
      };
      const tasks = [
        getOrgListCached().then(d => { if (d) this.orgOptions = freezeList(d); }),
        getSupplierListCached().then(d => { if (d) this.supplierOptions = freezeList(d); }),
        getCollectWayCached().then(d => { if (d) this.collectWayOptions = freezeList(d); })
      ];
      try { await Promise.all(tasks); } catch (e) { /* ignore */ }
    },
    async loadMeetingAndOpenCreate(meetingId) {
      try {
        const resp = await getMeetingDetail(meetingId);
        if (resp.code === 200 && resp.data) {
          // resp.data.meeting 才是 Meeting 实体本身
          const m = resp.data.meeting || resp.data;
          this.prefillMeeting = {
            id: m.id,
            orgId: m.orgId,
            projectId: m.projectId,
            meetingName: m.meetingName
          };
          this.createVisible = true;
        }
      } catch (e) { /* ignore */ }
    },

    onMainTabChange() {
      if (this.mainTab === 'stats' && this.$refs.stats) {
        this.$refs.stats.refresh();
      }
    },
    onTypeTabChange() { /* list 子组件 watch settlementType 自动刷新 */ },

    openCreate() { this.createVisible = true; },
    onCreated(order) {
      this.reloadCurrentList();
      if (order && order.id) {
        this.detailOrderId = order.id;
        this.detailVisible = true;
      }
    },
    openDetail(row) {
      this.detailOrderId = row.id;
      this.detailVisible = true;
    },
    async quickSubmit(row) {
      try {
        await this.$confirm('提交后将触发 AI 审核, 确认?', '提示', { type: 'warning' });
        const resp = await submitSpendingOrder(row.id);
        if (resp.code === 200) {
          this.$message.success('已提交');
          this.reloadCurrentList();
        } else this.$message.error(resp.msg || '提交失败');
      } catch (e) { /* cancel */ }
    },
    openHistory(row) {
      this.mainTab = 'audit';
      this.auditOrderId = String(row.id);
      this.auditViewOrderId = row.id;
    },
    loadAuditOrderId() {
      const id = Number(this.auditOrderId);
      this.auditViewOrderId = id > 0 ? id : null;
    },
    openAiAudit(order) {
      this.aiAuditOrderId = order.id;
      this.aiAuditVisible = true;
    },
    reloadCurrentList() {
      const ref = this.settlementType === 'LABOR' ? 'laborList' : 'supplierList';
      if (this.$refs[ref] && this.$refs[ref].reload) this.$refs[ref].reload();
    }
  }
};
</script>

<style scoped>
.spending-manage { padding: 16px; background: #F5F7FA; min-height: 100%; }
/* .page-header 已抽到 PageHeader 组件 */
.audit-finder { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
.audit-finder .tip { color: #909399; font-size: 12px; }
.empty { color: #909399; text-align: center; padding: 60px 0; }
</style>
